import { computed, ref, watch } from 'vue';
import { useStorage } from '@vueuse/core';
import { isEqual } from 'lodash-es';
import { useRoute } from 'vue-router';
import { ACTIVE_CART_ROUTES } from '@CartModule';
import { useActiveFilter, useFilters } from '@FilterModule';
import useDataTable from './useDataTable';

const { activeFilters } = useActiveFilter();
const { columns } = useDataTable();
const { filtersPayload } = useFilters();

const pendingTimeouts: Map<string, NodeJS.Timeout> = new Map();

const noLocalStorageRoutes = [...ACTIVE_CART_ROUTES];

// Local Storage
let currentLocalStore: any;
const currentRouteName = ref<string | undefined>('UNDEFINED');
const newStoreReady = ref(false);

// Columns
const activeColumns = ref<string[]>([]);
const activeColumnsEdited = ref<boolean>(false);
const initialColumns = ref<string[]>([]);

// DatePicker
const endDate = ref();
const endDateUnformatted = ref();
const singleDate = ref();
const startDate = ref();
const startDateUnformatted = ref();

// Filters
const filterUrl = ref();
const page = ref(1);
const searchQuery = ref('');
const selectFilter = ref();
// NOTE :: Each mounted ASelectFilter registers its default here (paramsLabel → defaultValue), so saved
// reports can tell a picked value from the default and put every select back to its own default on clear.
const selectFilterDefaults = ref<Record<string, string>>({});
const updatePaginator = ref();

// Sort
const activeSortField = ref<string>('');
const activeSortOrder = ref<'asc' | 'desc'>('desc');
const sort = ref<string>();

// Other
const size = ref<'compact' | 'default' | 'large'>('default');

const defaultVisibleColumns = computed(() => {
  const activeColumns: Set<string> = new Set();

  if (!columns.value?.length) return [];

  columns.value.forEach(({ field, visible, hidden }) => !!visible && !hidden && activeColumns.add(field));
  return Array.from(activeColumns);
});

const mainRequestUrlParams = computed(() => {
  let params = '';

  if (sort.value) params += `&sort=${sort.value}`;
  if (searchQuery.value) params += `&search=${encodeURIComponent(searchQuery.value)}`;
  if (filtersPayload.value.filterObj) params += getFilterObj(filtersPayload.value.filterObj);
  if (filtersPayload.value.specifics) params += `&${filtersPayload.value.specifics}`;
  if (filtersPayload.value?.kennelManager) params += `&kennelManager=${filtersPayload.value.kennelManager}`;
  // TODO :: Once Gerald change the expected response on endpoint, remove startDateUnformatted and endDateUnformatted
  if (startDateUnformatted.value) params += `&start_date=${startDateUnformatted.value}`;
  if (endDateUnformatted.value) params += `&end_date=${endDateUnformatted.value}`;
  if (page.value) params += `&page=${page.value}`;
  if (selectFilter.value?.value || selectFilter.value)
    params += selectFilter.value.label
      ? `&${selectFilter.value.label}=${selectFilter.value.value}`
      : selectFilter.value;

  return params;
});

watch(currentRouteName, (routeName) => onInitiLocalStorage(routeName));

watch(activeColumns, (newVal) => onPropertyUpdate('activeColumns', newVal));
watch(activeFilters, (newVal) => onPropertyUpdate('activeFilters', newVal));
watch(columns, (newVal) => onPropertyUpdate('columns', newVal));
watch(endDate, (newVal) => onPropertyUpdate('endDate', newVal));
watch(endDateUnformatted, (newVal) => onPropertyUpdate('endDateUnformatted', newVal));
watch(filterUrl, (newVal) => onPropertyUpdate('filterUrl', newVal));
watch(initialColumns, (newVal) => onPropertyUpdate('initialColumns', newVal));
watch(searchQuery, (newVal) => onPropertyUpdate('searchQuery', newVal));
watch(selectFilter, (newVal) => onPropertyUpdate('selectFilter', newVal));
watch(size, (newVal) => onPropertyUpdate('size', newVal));
watch(startDate, (newVal) => onPropertyUpdate('startDate', newVal));
watch(startDateUnformatted, (newVal) => onPropertyUpdate('startDateUnformatted', newVal));
watch(activeSortField, (newVal) => onPropertyUpdate('activeSortField', newVal));
watch(activeSortOrder, (newVal) => onPropertyUpdate('activeSortOrder', newVal));
watch(sort, (newVal) => onPropertyUpdate('sort', newVal));

// HELPERS
function setInitialRefsValue() {
  if (!currentLocalStore) return;

  activeColumns.value = currentLocalStore.value.activeColumns;
  activeFilters.value = currentLocalStore.value.activeFilters;
  columns.value = currentLocalStore.value.columns;
  endDate.value = currentLocalStore.value.endDate;
  endDateUnformatted.value = currentLocalStore.value.endDateUnformatted;
  filterUrl.value = currentLocalStore.value.filterUrl;
  initialColumns.value = currentLocalStore.value.initialColumns;
  searchQuery.value = currentLocalStore.value.searchQuery;
  selectFilter.value = currentLocalStore.value.selectFilter;
  size.value = currentLocalStore.value.size;
  startDate.value = currentLocalStore.value.startDate;
  startDateUnformatted.value = currentLocalStore.value.startDateUnformatted;
  activeSortField.value = currentLocalStore.value.activeSortField;
  activeSortOrder.value = currentLocalStore.value.activeSortOrder;
  sort.value = currentLocalStore.value.sort;

  newStoreReady.value = true;
}

function getStorageKey(name?: string) {
  return name ? `af-filters-${name}` : 'UNDEFINED';
}

function getFilterObj(filterObj: string | object) {
  const parsed = typeof filterObj === 'object' ? filterObj : JSON.parse(`{${filtersPayload.value.filterObj}}`);

  const encoded = encodeURIComponent(JSON.stringify(parsed));

  return `&filterObj=${encoded}`;
}

// EVENTS
function onInitiLocalStorage(routeName?: string) {
  pendingTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
  pendingTimeouts.clear();

  if (noLocalStorageRoutes.includes(routeName)) return;

  newStoreReady.value = false;

  currentLocalStore = useStorage(getStorageKey(routeName), {
    endDateUnformatted: '',
    startDateUnformatted: '',
    startDate: '',
    endDate: '',
    filterUrl: '',
    selectFilter: '',
    searchQuery: '',
    activeColumns: [],
    initialColumns: [],
    columns: [],
    activeFilters: <any[]>[],
    size: 'default',
    activeSortField: '',
    activeSortOrder: 'desc',
    sort: undefined,
  });

  setInitialRefsValue();
}

function onPropertyUpdate(property: string, value: any) {
  if (!newStoreReady.value) return;
  if (!currentLocalStore?.value) return;

  if (pendingTimeouts.has(property)) {
    clearTimeout(pendingTimeouts.get(property)!);
    pendingTimeouts.delete(property);
  }

  const timeoutId = setTimeout(() => {
    if (property === 'columns') return onColumnsUpdate(value);

    currentLocalStore.value[property] = value;

    pendingTimeouts.delete(property);
  }, 500);

  pendingTimeouts.set(property, timeoutId);
}

function onColumnsUpdate(value: any) {
  const columnsInStorage = currentLocalStore.value?.columns?.length ? currentLocalStore.value.columns : columns.value;

  const apiColumns = columns.value.map(({ field }) => field);
  const localColumns = columnsInStorage.map((column: any) => column.field);
  const newColumns = isEqual(apiColumns, localColumns);

  if (Array.isArray(columnsInStorage) && !newColumns) {
    columnsInStorage.forEach((col: any) => {
      col.visible = activeColumns.value.includes(col.field);
    });
  }

  currentLocalStore.columns = newColumns ? value : columnsInStorage;
}

export default function () {
  const ROUTE = useRoute();

  updateRouteName();

  function updateRouteName() {
    if (!ROUTE?.name) return;

    const routeName = ROUTE.name.toString();

    if (routeName === currentRouteName.value) return;

    currentRouteName.value = routeName;
  }

  function reset() {
    newStoreReady.value = false;

    // Columns
    activeColumns.value = [];
    initialColumns.value = [];
    activeColumnsEdited.value = false;

    // DatePicker
    endDateUnformatted.value = '';
    startDateUnformatted.value = '';
    endDate.value = '';
    startDate.value = '';
    singleDate.value = '';

    // Filters
    filterUrl.value = '';
    page.value = 1;
    searchQuery.value = '';
    selectFilter.value = undefined;
    selectFilterDefaults.value = {};
    updatePaginator.value = undefined;

    // Sort
    activeSortField.value = '';
    activeSortOrder.value = 'desc';
    sort.value = undefined;

    // Other
    size.value = 'default';

    const routeName = ROUTE?.name?.toString();
    if (routeName) onInitiLocalStorage(routeName);
  }

  return {
    activeColumns,
    activeColumnsEdited,
    activeSortField,
    activeSortOrder,
    currentLocalStore,
    defaultVisibleColumns,
    endDate,
    endDateUnformatted,
    filterUrl,
    initialColumns,
    page,
    searchQuery,
    selectFilter,
    selectFilterDefaults,
    singleDate,
    size,
    sort,
    startDate,
    startDateUnformatted,
    updatePaginator,

    mainRequestUrlParams,

    reset,
  };
}
